import i18n from 'i18n-js';

import { OVERLAYS } from '../../constants';
import {
  AnalyticsProvider,
  FetchProvider,
  SocketProvider,
} from '../../providers';
import mergeDeep from '../../utils/mergeDeep';

import {
  BUILDER_ERROR,
  BUILDER_INFO,
  BUILDER_REQUEST,
  BUILDER_RESET,
  BUILDER_SET,
  BUILDER_SET_CHAT,
  BUILDER_SUCCESS,
  BUILDER_UPDATED,
} from './types';
import { userSet } from '../User/actions';

export const builderError = (error) => ({
  type: BUILDER_ERROR,
  error,
});

export const builderInfo = (info) => ({
  type: BUILDER_INFO,
  info,
});

export const builderRequest = {
  type: BUILDER_REQUEST,
};

export const builderReset = {
  type: BUILDER_RESET,
};

export const builderSet = (data) => ({
  type: BUILDER_SET,
  payload: data,
});

export const builderSetChat = (data) => ({
  type: BUILDER_SET_CHAT,
  payload: data,
});

export const builderSuccess = (data) => ({
  type: BUILDER_SUCCESS,
  payload: data,
});

export const builderUpdated = (data) => ({
  type: BUILDER_UPDATED,
  payload: data,
});

export const createOverlay = (template, history) => async (dispatch, getState) => {
  dispatch(builderRequest);
  try {
    const language = navigator.language || navigator.userLanguage;
    const result = await FetchProvider.fetchPost('overlay', {
      language,
      template,
    });

    await dispatch(builderSuccess(result));
    history.push(`/builder/${result.id}`);
    return;
  } catch (e) {
    if (!e?.response)
      return dispatch(builderError(i18n.t('common.connectionError')));
    
    // Handle subscription limit exceeded - redirect to subscription page
    if (e?.response?.status === 403) {
      dispatch(builderError(null)); // Clear any loading state
      history.push('/subscription');
      return;
    }
    
    if (e?.response?.status === 400 || e?.response?.status === 404)
      return dispatch(builderError(i18n.t('screens.builder.error')));

    return dispatch(builderError(i18n.t('screens.builder.notFound')));
  }
};

export const getOverlay =
  (id, noSocket, history) => async (dispatch, getState) => {
    dispatch(builderRequest);
    try {
      const result = await FetchProvider.fetchGet(`overlay/${id}`);

      if (result?.data?.fileToDownload) {
        window.open(result.data.fileToDownload, '_blank');
        history.push('/dashboard');
        return;
      }

      await dispatch(
        builderSuccess({
          ...result,
          ...OVERLAYS.DESIGNS_DATA[result.key],
          id: result._id,
        })
      );

      if (noSocket) {
        return;
      }

      if (
        result.category === 'ai' ||
        result.category === 'question' ||
        result.category === 'giveaway' ||
        result.category === 'quiz' ||
        result.category === 'wordguessing' ||
        result.category === 'countdown'
      ) {
        await SocketProvider.connect(result._id);
        SocketProvider.listen('builderChange', async (data) => {
          await dispatch(
            builderSuccess({
              ...data,
              ...OVERLAYS.DESIGNS_DATA[data.key],
              id: data._id,
            })
          );
        });
      }
      if (result.category === 'question') {
        await SocketProvider.connect(result._id);
        SocketProvider.listen('twitchChat', async (data) => {
          const { builder } = getState();
          const userFound = builder.chat.find(
            (chatMessage) => chatMessage.user === data.user
          );

          await dispatch(
            builderSetChat({
              ...data,
              color: userFound ? userFound.color : data.color,
            })
          );
        });
      }
      return;
    } catch (e) {
      if (!e?.response)
        return dispatch(builderError(i18n.t('common.connectionError')));
      if (e?.response?.status === 400 || e?.response?.status === 404)
        return dispatch(builderError(i18n.t('screens.builder.notFound')));

      return dispatch(builderError(i18n.t('screens.builder.notFound')));
    }
  };

export const sendAction = (overlayId, action) => async (dispatch) => {
  dispatch(builderRequest);
  try {
    const result = await FetchProvider.fetchPut(
      `overlay/streamdeck/${overlayId}`,
      {
        action,
      }
    );

    if (result?.updated) {
      await dispatch(builderUpdated(true));
    } else {
      await dispatch(builderError(null));
    }

    return;
  } catch (e) {
    return dispatch(builderError(i18n.t('screens.builder.notUpdated')));
  }
};

var myVar;
export const updateOverlay = (data) => async (dispatch, getState) => {
  try {
    clearTimeout(myVar);
    myVar = setTimeout(() => {
      const { builder } = getState();
      const result = mergeDeep(builder.data, data);
      FetchProvider.fetchPut(`overlay/${builder.data.id}`, {
        data: result.data,
        settings: result.settings,
        styles: result.styles,
      });
      AnalyticsProvider.event('UPDATE_OVERLAY', {
        label: result.key,
        category: result.category,
        key: result.key,
        type: result.type,
      });
    }, 350);

    await dispatch(builderSet(data));
    return;
  } catch (e) {
    return dispatch(builderError(i18n.t('screens.builder.notUpdated')));
  }
};

export const uploadFiles =
  (files, onFinishUpload, ...rest) =>
  async (dispatch) => {
    try {
      dispatch(builderRequest);
      const result = await FetchProvider.fetchFiles('user/upload', files);
      if (result.errors) {
        await dispatch(
          builderError(
            i18n.t('screens.builder.filesNotLoaded', {
              files: result.errors,
            })
          )
        );
      }
      if (result.data) {
        onFinishUpload(result.data, ...rest);
        if (!result.errors) dispatch(builderError(null));
      }
    } catch (e) {
      return dispatch(builderError(i18n.t('screens.builder.notUpdated')));
    }
  };

export const updateAIBackground =
  (prompt, value, itemValue) => async (dispatch, getState) => {
    try {
      const {
        user: { data: userData },
      } = getState();

      dispatch(builderRequest);
      const result = await FetchProvider.fetchPost('replicate/image', {
        prompt,
      });

      if (!result.generatedImage)
        return dispatch(builderError(i18n.t('screens.builder.notUpdated')));

      dispatch(
        updateOverlay({
          styles: { [value]: { [itemValue]: result.generatedImage } },
        })
      );
      dispatch(userSet({ ocoins: userData.ocoins - 1 }));
    } catch (e) {
      return dispatch(builderError(i18n.t('screens.builder.notUpdated')));
    }
  };

export const updateAIQuiz =
  ({ prompt }) =>
  async (dispatch, getState) => {
    try {
      const {
        user: { data: userData },
      } = getState();
      const language = navigator.language || navigator.userLanguage;

      dispatch(builderRequest);
      const result = await FetchProvider.fetchPost('replicate/quiz', {
        language,
        prompt,
      });

      if (!result.generatedQuiz || !result.generatedQuiz.length)
        return dispatch(builderError(i18n.t('screens.builder.notUpdated')));

      dispatch(
        updateOverlay({
          data: { questions: result.generatedQuiz },
        })
      );
      dispatch(userSet({ ocoins: userData.ocoins - 1 }));
    } catch (e) {
      return dispatch(builderError(i18n.t('screens.builder.notUpdated')));
    }
  };

export const updateAIOverlay = () => async (dispatch, getState) => {
  try {
    const {
      builder: { data: builderData },
      user: { data: userData },
    } = getState();
    const language = navigator.language || navigator.userLanguage;

    dispatch(builderRequest);
    const result = await FetchProvider.fetchPost('replicate/overlay', {
      language,
      prompt: builderData.data.prompt,
      type: builderData.type,
    });

    if (!result.generatedImages)
      return dispatch(builderError(i18n.t('screens.builder.notUpdated')));

    dispatch(
      updateOverlay({
        data: {
          image: result.generatedImages[0],
          generatedImages: result.generatedImages,
        },
      })
    );
    dispatch(userSet({ ocoins: userData.ocoins - 1 }));
  } catch (e) {
    return dispatch(builderError(i18n.t('screens.builder.notUpdated')));
  }
};

export const updateAIOverlayVideo = () => async (dispatch, getState) => {
  try {
    const {
      builder: { data: builderData },
      user: { data: userData },
    } = getState();
    const language = navigator.language || navigator.userLanguage;

    dispatch(builderRequest);
    const result = await FetchProvider.fetchPost('replicate/overlay-video', {
      language,
      prompt: builderData.data.prompt,
      type: builderData.type,
      overlayId: builderData.id,
    });

    if (result.success) {
      dispatch(builderInfo(i18n.t('screens.builder.build.ai.aimodal.videoGenerating')));
      dispatch(userSet({ ocoins: userData.ocoins - 10 }));
    } else {
      return dispatch(builderError(i18n.t('screens.builder.notUpdated')));
    }
  } catch (e) {
    return dispatch(builderError(i18n.t('screens.builder.notUpdated')));
  }
};

export const upscaleAIOverlay = () => async (dispatch, getState) => {
  try {
    const {
      builder: { data: builderData },
    } = getState();

    dispatch(builderRequest);
    const result = await FetchProvider.fetchPost('replicate/upscale', {
      image: builderData.data.image,
    });

    if (!result.upscaledImage)
      return dispatch(builderError(i18n.t('screens.builder.notUpdated')));

    dispatch(
      updateOverlay({
        data: {
          image: result.upscaledImage,
          generatedImages: [],
          historyImages: [
            ...builderData.data.historyImages,
            result.upscaledImage,
          ],
        },
      })
    );
  } catch (e) {
    return dispatch(builderError(i18n.t('screens.builder.notUpdated')));
  }
};
